import { Col, Form, Row } from "react-bootstrap";
import AudienceCard from "./AudienceCard";
import { useMemo, useState } from "react";
import Pagination from "../Pagination";
import AudienceForm from "./AudienceForm";
import { Button } from "../../../../components/core/basic/Button";
import { useAudienceTemplates } from "./useAudienceTemplates";
import { useEnabledModules, useModuleEnabled } from "../../../../redux/selectors/modules";
import checkModule from "../../../../utils/checkModule";
import { Link } from "@tanstack/react-router";
import { HStack } from "../../../../../styled-system/jsx";
import { buttonRecipe } from "../../../../components/core/basic/Button";
import { styled } from "../../../../../styled-system/jsx";
import { Modal } from "../../../../components/core/basic/Modal";
import { useTranslation } from "react-i18next";

const StyledLink = styled(Link, buttonRecipe);

const PAGE_SIZE = 4;

const AudiencesSection = () => {
  const { t } = useTranslation(["amc"]);
  const [audienceSearch, setAudienceSearch] = useState("");
  const [showAudienceForm, setShowAudienceForm] = useState(false);
  const [showCustomAudienceModal, setShowCustomAudienceModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const userModules = useEnabledModules();
  const hasAMCFirstPartyData = useModuleEnabled("AMC_FIRSTPARTY");

  const { templates } = useAudienceTemplates();

  const filteredAudiences = useMemo(() => {
    const filteredAudiences =
      templates?.filter((audience) => {
        if (audience.required_module) {
          return checkModule(userModules, audience.required_module);
        }
        return true;
      }) ?? [];
    if (audienceSearch) {
      return filteredAudiences.filter((audience) => {
        const { audience_template_name, audience_byline, audience_display_name, audience_description } = audience;

        return `${audience_template_name} ${audience_byline} ${audience_display_name} ${audience_description}`
          .toLowerCase()
          .includes(audienceSearch.toLowerCase());
      });
    }
    return filteredAudiences;
  }, [audienceSearch, templates, userModules]);

  const paginatedAudiences = useMemo(() => {
    return filteredAudiences?.slice((currentPage - 1) * PAGE_SIZE, (currentPage - 1) * PAGE_SIZE + PAGE_SIZE);
  }, [filteredAudiences, currentPage]);

  return (
    <Row className="pb-5">
      <Col xs={12}>
        <div className="d-flex justify-content-between">
          <h3 className="m-0 py-2 ps-5 fs-2" style={{ fontWeight: 600 }}>
            {t(($) => $.amc["Audience Templates"])}
          </h3>
          <Modal
            isOpen={showCustomAudienceModal}
            onOpenChange={(isOpen) => setShowCustomAudienceModal(isOpen)}
            title={t(($) => $.amc["Request a Custom Audience"])}
            css={{ width: "60vw", padding: "xlarge" }}
          >
            <Row className="pb-5 d-flex justify-content-center gap-3">
              <p className="text-center">
                {t(
                  ($) =>
                    $.amc[
                      "To have a custom audience template created, please contact your customer success representative and provide a description of the template you would like to have generated."
                    ],
                )}
              </p>
              <Button onPress={() => setShowCustomAudienceModal(false)}>{t(($) => $.amc["Continue"])}</Button>
            </Row>
          </Modal>
          <HStack gap="3">
            {hasAMCFirstPartyData && (
              <StyledLink to="/user/amc/configuration">{t(($) => $.amc["Manage First Party Data"])}</StyledLink>
            )}
            <Button onPress={() => setShowCustomAudienceModal(true)}>
              {t(($) => $.amc["Request a Custom Template"])}
            </Button>
          </HStack>

          <Modal
            isOpen={showAudienceForm}
            onOpenChange={(isOpen) => setShowAudienceForm(isOpen)}
            css={{ width: "60vw", padding: "xlarge" }}
          >
            <AudienceForm
              setShowAudienceForm={setShowAudienceForm}
              defaultFormData={
                typeof showAudienceForm === "string" ?
                  {
                    template_name: showAudienceForm,
                  }
                : {}
              }
            />
          </Modal>
        </div>
        <Row>
          <Col xs={12} md={6} xl={4} className="py-3 px-5">
            <Form.Control
              type="search"
              placeholder={t(($) => $.amc["Search for an available audience..."])}
              onChange={(e) => {
                setAudienceSearch(e.target.value);
              }}
              style={{ borderRadius: "5px" }}
            />
          </Col>
          {filteredAudiences?.length > PAGE_SIZE && (
            <Col xs={12} md={6} xl={8} className={"d-flex justify-content-end"}>
              <Pagination
                data={filteredAudiences}
                pageSize={PAGE_SIZE}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </Col>
          )}
        </Row>
        <Row>
          {paginatedAudiences?.map((audience, index) => {
            return <AudienceCard audience={audience} key={index} setShowAudienceForm={setShowAudienceForm} />;
          })}
        </Row>
      </Col>
    </Row>
  );
};

export default AudiencesSection;
